<template>
  <div ref="rootRef" :class="dialogClasses">
    <Dialog
      ref="dialogComponentRef"
      v-model:visible="dialogVisible"
      :modal="true"
      :style="dialogStyle"
      :contentClass="contentClasses"
      @hide="onHide"
      @show="onDialogShow"
      v-bind="filteredProps"
      append-to="self"
      :data-testid="dataTestId"
    >
      <template #header>
        <div
          class="dialog-header"
          data-overlay-drag-handle
          draggable="false"
          @mousedown="onHeaderMouseDown"
        >
          <h3 class="dialog-title">{{ title }}</h3>
          <div
            v-if="$slots['header-actions']"
            class="dialog-header-window-actions"
            data-no-overlay-drag
            data-pc-section="headeractions"
            @mousedown.stop
            @pointerdown.stop
          >
            <slot name="header-actions" />
          </div>
        </div>
      </template>

      <div class="m-dialog-content-shell">
        <div ref="contentRootRef" class="m-dialog-body-wrap">
          <div class="dialog-content">
            <slot name="body">
              <div>Содержимое диалога</div>
            </slot>
          </div>
        </div>
        <div
          v-if="resizable"
          class="m-dialog-resize-handle"
          data-no-overlay-drag
          aria-hidden="true"
          :title="t('ui.resize')"
          @pointerdown.stop.prevent="emit('resize-start', $event)"
        >
          <svg
            class="m-dialog-resize-handle__icon"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="1.35" fill="currentColor" />
            <circle cx="7" cy="11" r="1.35" fill="currentColor" />
            <circle cx="11" cy="7" r="1.35" fill="currentColor" />
            <circle cx="3" cy="11" r="1.35" fill="currentColor" />
            <circle cx="7" cy="7" r="1.35" fill="currentColor" />
            <circle cx="11" cy="3" r="1.35" fill="currentColor" />
          </svg>
        </div>
      </div>

      <template #footer v-if="$slots.buttons">
        <div class="dialog-footer">
          <slot name="buttons" />
        </div>
      </template>
    </Dialog>

  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@/common/composables/useI18n'
import { PLATFORM_DIALOG_DRAGGING_CLASS, usePlatformDialogDrag } from '@/common/composables/usePlatformDialogDrag'
import Dialog from 'primevue/dialog'
import { computed, nextTick, onBeforeUnmount, ref, useAttrs, watch } from 'vue'

interface Props {
  modelValue?: boolean
  title?: string
  fullscreen?: boolean
  hideOverlay?: boolean
  scrollable?: boolean
  maxWidth?: string
  width?: string
  height?: string
  maxHeight?: string
  closable?: boolean
  dismissableMask?: boolean
  closeOnEscape?: boolean
  resizable?: boolean
  draggable?: boolean
  dataTestId?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  title: '',
  fullscreen: false,
  hideOverlay: false,
  scrollable: true,
  maxWidth: '1300px',
  width: 'auto',
  height: '',
  maxHeight: '',
  closable: true,
  dismissableMask: false,
  closeOnEscape: false,
  resizable: false,
  draggable: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  hide: []
  'resize-start': [event: PointerEvent]
}>()

const dialogComponentRef = ref<{ $el?: HTMLElement } | null>(null)
const contentRootRef = ref<HTMLElement | null>(null)
const rootRef = ref<HTMLElement | null>(null)
const dialogSurfaceRef = ref<HTMLElement | null>(null)
let maskFocusGuardEl: HTMLElement | null = null

const preventMaskFocusSteal = (event: Event) => {
  const target = event.target
  if (!(target instanceof HTMLElement)) return
  // Capture on mask runs for clicks inside .p-dialog too; only block backdrop clicks.
  if (target.closest('.p-dialog')) return
  event.preventDefault()
}

const resolveDialogMask = (): HTMLElement | null => {
  const root = rootRef.value
  if (!root) return null
  return (
    root.querySelector<HTMLElement>('.p-dialog-mask') ??
    root.querySelector<HTMLElement>('.p-overlay-mask')
  )
}

const bindMaskFocusGuard = () => {
  unbindMaskFocusGuard()
  if (props.dismissableMask) return
  const mask = resolveDialogMask()
  if (!mask) return
  maskFocusGuardEl = mask
  mask.addEventListener('mousedown', preventMaskFocusSteal, true)
  mask.addEventListener('pointerdown', preventMaskFocusSteal, true)
}

const unbindMaskFocusGuard = () => {
  if (!maskFocusGuardEl) return
  maskFocusGuardEl.removeEventListener('mousedown', preventMaskFocusSteal, true)
  maskFocusGuardEl.removeEventListener('pointerdown', preventMaskFocusSteal, true)
  maskFocusGuardEl = null
}

const attrs = useAttrs()
const { t } = useI18n()

const filteredProps = computed(() => {
  const { modelValue, title, maxWidth, width, height, maxHeight, resizable, draggable: _draggable, ...rest } = props
  return { ...rest, ...attrs, draggable: false }
})

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
  },
})

const { onHeaderMouseDown } = usePlatformDialogDrag()

const resolveDialogSurface = (): HTMLElement | null => {
  const fromRoot = rootRef.value?.querySelector<HTMLElement>('.p-dialog')
  if (fromRoot) {
    return fromRoot
  }

  const componentRoot = dialogComponentRef.value?.$el
  if (!(componentRoot instanceof HTMLElement)) {
    return null
  }
  return componentRoot.classList.contains('p-dialog')
    ? componentRoot
    : componentRoot.querySelector<HTMLElement>('.p-dialog')
}

const refreshDialogSurface = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      dialogSurfaceRef.value = resolveDialogSurface()
    })
  })
}

const onDialogShow = () => {
  refreshDialogSurface()
  nextTick(() => {
    requestAnimationFrame(() => {
      bindMaskFocusGuard()
    })
  })
}

watch(
  dialogVisible,
  (visible) => {
    if (!visible) {
      dialogSurfaceRef.value = null
      unbindMaskFocusGuard()
      return
    }
    refreshDialogSurface()
    nextTick(() => {
      requestAnimationFrame(() => {
        bindMaskFocusGuard()
      })
    })
  },
  { immediate: true },
)

const dialogStyle = computed(() => {
  const style: Record<string, string> = {
    width: props.width,
    maxWidth: props.maxWidth,
  }
  if (props.height) {
    style.height = props.height
  }
  if (props.maxHeight) {
    style.maxHeight = props.maxHeight
  }
  return style
})

const dialogClasses = computed(() => [
  'm-dialog',
  {
    'm-dialog--fullscreen': props.fullscreen,
    'm-dialog--scrollable': props.scrollable,
  },
])

const contentClasses = computed(() => 'm-dialog-content')
const dataTestId = computed(() => props.dataTestId || undefined)

const closeDialog = () => {
  dialogVisible.value = false
}

const onHide = () => {
  unbindMaskFocusGuard()
  document.documentElement.classList.remove(PLATFORM_DIALOG_DRAGGING_CLASS)
  emit('hide')
}

onBeforeUnmount(() => {
  unbindMaskFocusGuard()
})

defineExpose({
  getDialogEl: () => dialogSurfaceRef.value ?? resolveDialogSurface(),
  getContentEl: () => contentRootRef.value,
})
</script>

<style scoped lang="scss">
.m-dialog-content {
  background-color: #ffffff;
  border-color: #d1d5db;
}

.m-dialog {
  --p-dialog-background: #ffffff;
  :deep(.p-dialog) {
    .p-dialog-header {
      --p-button-text-secondary-hover-background: #4e4e4e19;
      padding: 1.5rem 1.5rem 0.5rem 1.5rem;
      border-bottom: 1px solid #e5e7eb;
      cursor: move;
      touch-action: none;
      user-select: none;

      :deep(.p-dialog-header-actions) {
        cursor: default;
        user-select: auto;
        touch-action: auto;
      }

      .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex: 1 1 auto;
        min-width: 0;
        cursor: move;
        touch-action: none;

        .dialog-title {
          margin: 0;
          margin-right: auto;
          font-size: 1.25rem;
          font-weight: 600;
          color: #374151;
        }

        .dialog-header-window-actions {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          gap: 0.125rem;
          margin-right: 0.125rem;
          cursor: default;

          :deep(.p-button) {
            width: 2rem;
            height: 2rem;
            color: #6b7280;

            &:hover {
              color: #374151;
              background: #f3f4f6;
            }
          }
        }

        .dialog-close {
          width: 2rem;
          height: 2rem;
          color: #6b7280;

          &:hover {
            color: #374151;
            background: #f3f4f6;
          }
        }
      }
    }

    .p-dialog-content {
      padding: 1.5rem;
      position: relative;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .m-dialog-content-shell {
        position: relative;
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .m-dialog-body-wrap {
        flex: 1 1 auto;
        min-height: 0;
        width: 100%;
        overflow-y: auto;
      }

      .dialog-content {
        min-height: 0;
      }
    }

    .p-dialog-footer {
      padding: 0 1.5rem 1.5rem 1.5rem;
      border-top: 1px solid #e5e7eb;

      .dialog-footer {
        display: flex;
        gap: 0.5rem;
        justify-content: flex-end;
      }
    }
  }

  &--fullscreen {
    :deep(.p-dialog) {
      width: 100vw !important;
      height: 100vh !important;
      max-width: none !important;

      .p-dialog-content {
        max-height: none;
        height: calc(100vh - 120px);
      }
    }
  }

  &--scrollable {
    :deep(.m-dialog-body-wrap) {
      overflow-y: auto;
    }
  }

  :deep(.p-dialog) {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  :deep(.p-dialog.p-dialog) {
    transition: none;
    overflow: hidden !important;
    max-height: none !important;
    border-radius: var(--app-radius-card, 10px);
  }
}
</style>

<style lang="scss">
.m-dialog-resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px 0 0 0;
  cursor: nwse-resize;
  z-index: 20;
  pointer-events: auto;
  touch-action: none;
  color: #9ca3af;
  background: linear-gradient(135deg, transparent 0 42%, rgb(249 250 251 / 92%) 42% 100%);
  opacity: 0.85;
  transition:
    color 0.15s ease,
    background-color 0.15s ease,
    opacity 0.15s ease;

  &__icon {
    width: 16px;
    height: 16px;
    pointer-events: none;
  }

  &:hover {
    opacity: 1;
    color: #4b5563;
    background: linear-gradient(135deg, transparent 0 38%, #f3f4f6 38% 100%);
  }

  &:active {
    color: #374151;
    background: linear-gradient(135deg, transparent 0 38%, #e5e7eb 38% 100%);
  }
}
</style>
